import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Fab,
    Grid,
    IconButton,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from "@material-ui/core";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";

import { blue } from "@material-ui/core/colors";
import { FiberManualRecord as DotIcon, Stop } from "@material-ui/icons";
import queryString from "query-string";
import { listEmployees as listProviderEmployees } from "../api/ProviderEmployeeAPI";
import { closeContract, getContract, getDocumentUrl } from "../api/TenantContractAPI";
import { EditSingleIcon } from "../components/Icons";
import Progress from "../components/Progress";
import { ErrorSnackbar, SuccessSnackbar, WarningSnackbar } from "../components/Snackbars";
import Surface from "../components/Surface";
import TabPanel, { a11yProps } from "../components/TabsGenerator";
import { AppContext } from "../context/AppContext";
import translate from "../i18n/Translator";
import { SEEmployeeRequest } from "../model/CfdiSe";
import { Contract, ContractStatus } from "../model/Contract";
import { Employee, EmployeesQueryParams } from "../model/Employee";
import { isRoleOrParent } from "../model/Role";
import { RouterParams } from "../router/RouterParams";
import ContractDetails from "./ContractDetails";
import ContractDocs from "./ContractDocs";
import ContractEmployees, { convertSeEmployee } from "./ContractEmployees";
import { TenantContractForm } from "./ContractForm";
import ContractOverviewMenu from "./ContractOverviewMenu";
import { EmployeeSnackbarProvider } from "../se_commons/EmployeesSnackbar";

const TABS_NAMES = ["details", "employees", "docs"] as const;

type TabName = (typeof TABS_NAMES)[number];

interface ContractTab {
    name: TabName;
    title: ReactNode;
}

export default function ContractOverview({ match }: RouterParams) {
    const history = useHistory();
    const context = useContext(AppContext);
    const location = useLocation();
    const tenantId = context.session?.tenant?.id || "-";
    const isProvider = isRoleOrParent(context.session!.role, "provider");
    const [tabs, setTabs] = useState<ContractTab[]>([]);

    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const queryStr = queryString.parse(window.location.search);

    const [status, setStatus] = useState<string>("loading");
    const [success, setSuccess] = useState<string>();
    const [warning, setWarning] = useState<string>();
    const [error, setError] = useState<string>();

    const [contract, setContract] = useState<Contract>();

    const [showCloseDialog, setShowCloseDialog] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [employees, setEmployees] = useState<Employee[]>([]);
    const [seEmployees, setSEEmployees] = useState<SEEmployeeRequest[]>();

    const [gridAnchorEl, setGridAnchorEl] = useState<null | HTMLElement>(null);
    const [showEditDialog, setShowEditDialog] = useState<boolean>(false);

    const tabName = query.get("tab");

    const tabToIndex = useCallback(
        (tab: TabName): number => {
            let index = tabs.findIndex((t) => t.name === tab);
            return Math.max(index, 0);
        },
        [tabs]
    );

    const tabQs = useCallback((): number => {
        const queryTab = query.get("tab") as TabName;
        if (TABS_NAMES.includes(queryTab)) {
            return tabToIndex(queryTab);
        }
        return 0;
    }, [query, tabToIndex]);

    const [value, setValue] = useState<number>(tabQs);

    const load = () => {
        setStatus("loading");
        getContract(tenantId, match.params.contractId)
            .then((contract) => {
                setContract(contract);
                listProviderEmployees(tenantId, contract.provider_id, 0, 0, { search: "" } as EmployeesQueryParams)
                    .then((employees) => {
                        setEmployees(employees.items);
                        setStatus("loaded");
                    })
                    .catch((error) => {
                        setStatus(error.message);
                    });
            })
            .catch((error) => {
                setStatus(error.message);
            });
    };

    useEffect(load, [match.params.contractId, tenantId]);

    useEffect(() => {
        if (!contract) {
            return;
        }
        const allTabs: ContractTab[] = [
            {
                name: "details",
                title: translate("se_contracts.view.tabs.details" as string),
            },
            {
                name: "employees",
                title: translate("se_contracts.view.tabs.employees") as string,
            },
            {
                name: "docs",
                title: translate("se_contracts.view.tabs.docs") as string,
            },
        ];
        const statusTabs = allTabs.map((el) => {
            if ("docs" === el.name) {
                const statuses = contract.specialized_services?.contributions?.map((c) => c.status) || ["PENDING"];
                const allCompletedStatuses = statuses.indexOf("PENDING") === -1;
                const allInvoices =
                    contract.specialized_services?.all_invoices ||
                    !contract.specialized_services?.employees ||
                    contract.specialized_services?.employees?.length === 0 ||
                    false;
                if (!allCompletedStatuses || !allInvoices) {
                    el.title = (
                        <Grid container>
                            <Box display="flex" alignItems="center">
                                <Grid item style={{ marginRight: "8px" }}>
                                    {el.title}
                                </Grid>
                                <Grid item>
                                    <DotIcon style={{ color: "red", fontSize: "12px", marginTop: "-15px" }} />
                                </Grid>
                            </Box>
                        </Grid>
                    );
                }
            }
            return el;
        });
        let i = 0;
        setSEEmployees(
            contract.specialized_services?.employees
                ?.filter((seEmployee) => Boolean(seEmployee.employee))
                .map((seEmployee) => {
                    return { ...convertSeEmployee(seEmployee, i++), deletable: !isProvider };
                })
        );
        setTabs(statusTabs);
    }, [contract, isProvider]);

    useEffect(() => {
        setValue(tabQs());
    }, [tabName, tabQs, tabs]);

    const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        let newTab = tabs[newValue];
        if (newTab) {
            query.set("tab", newTab.name);
        }
        setQuery("tab", newTab.name);
        setValue(newValue);
    };

    const onUpdateContract = (contract: Contract) => {
        setContract(contract);
        setShowEditDialog(false);
        setSuccess(translate("se_contracts.view.success_update_contract") as string);
    };

    const onUpdateEmployees = (contract: Contract) => {
        setContract(contract);
    };

    const tabContent = (index: number) => {
        if (!contract) return;

        if (!context.session || !context.session.tenant) return;

        const current = tabs[index];
        if (!current) return;

        switch (current.name) {
            case "details":
                return <ContractDetails contract={contract} queryStr={queryString.stringify(queryStr)} />;
            case "employees":
                return (
                    <EmployeeSnackbarProvider>
                        <ContractEmployees
                            contract={contract}
                            queryStr={queryString.stringify(queryStr)}
                            employees={employees}
                            onSubmit={onUpdateEmployees}
                            setSEEmployees={setSEEmployees}
                            seEmployees={seEmployees}
                        />
                    </EmployeeSnackbarProvider>
                );
            case "docs":
                return <ContractDocs contract={contract} uploadSipare={contract.provider?.upload_sipare || false} />;
            default:
                return <div>{current.name}</div>;
        }
    };

    const chipStatus = () => {
        if (!contract) {
            return <></>;
        }

        let color = "inherit";
        if (ContractStatus.PENDING_DOCS === contract.status) {
            color = "red";
        } else if (ContractStatus.VALID === contract.status) {
            color = "green";
        } else if (ContractStatus.TO_START === contract.status) {
            color = blue[500];
        }

        return (
            <Grid container alignItems="center">
                <Grid item>{contract.provider?.name || ""}</Grid>
                <Grid item xs="auto">
                    <Box mx={2}>
                        <Chip
                            label={translate(`se_contracts.status.${contract.status}`) as string}
                            variant="outlined"
                            size="small"
                            style={{ color, borderColor: color }}
                        />
                    </Box>
                </Grid>
            </Grid>
        );
    };

    if (status === "loading") {
        return <Progress />;
    }

    if (status !== "loaded" || !contract) {
        return (
            <Typography variant="body1" component="h5" color="error" align="center">
                {status}
            </Typography>
        );
    }

    const onClickEdit = () => {
        if (!contract) return;

        if (!context.isGranted("TenantContractsUpdate")) {
            return;
        }

        setShowEditDialog(true);
    };

    const onCloseEditDialog = () => {
        setShowEditDialog(false);
    };

    const setQuery = (key: string, value: string) => {
        query.set(key, value);
        history.replace({
            search: query.toString(),
        });
    };

    const onClickClose = () => {
        if (!contract) {
            return;
        }
        if (ContractStatus.VALID !== contract.status) {
            return;
        }
        setShowCloseDialog(true);
    };

    const onConfirmClose = () => {
        if (!contract) {
            return;
        }
        setSubmitting(true);
        closeContract(context.session!.tenant!.id, contract.id)
            .then((contract) => {
                setContract(contract);
                setSuccess(translate("se_contracts.view.close.success") as string);
            })
            .catch((error) => {
                setError(error.message);
            })
            .finally(() => {
                setSubmitting(false);
                setShowCloseDialog(false);
            });
    };

    const onClickedMore = (event: React.MouseEvent<HTMLElement>) => {
        setGridAnchorEl(event.currentTarget);
    };

    const onCloseSnackbars = () => {
        setSuccess(undefined);
        setWarning(undefined);
        setError(undefined);
    };

    return (
        <Surface
            title={<strong>{contract.description}</strong>}
            subtitle={chipStatus()}
            backButton
            icon={<MoreVertIcon />}
            className="PaperPagination PaperTabContainer"
            titleActions={
                <Grid container alignItems="center" spacing={1}>
                    {ContractStatus.CLOSED !== contract.status &&
                        !isProvider &&
                        context.isGranted("TenantContractsUpdate") && (
                            <Grid item>
                                <Fab
                                    color="primary"
                                    size="small"
                                    title={translate("buttons.edit") as string}
                                    onClick={onClickEdit}
                                >
                                    <EditSingleIcon />
                                </Fab>
                            </Grid>
                        )}
                    {ContractStatus.CLOSED !== contract.status &&
                        !isProvider &&
                        context.isGranted("TenantContractsUpdate") && (
                            <Grid item>
                                <Tooltip
                                    title={
                                        ContractStatus.VALID === contract.status
                                            ? ""
                                            : (translate("se_contracts.view.close_disabled", {
                                                  status: translate(`se_contracts.status.${ContractStatus.VALID}`),
                                              }) as string)
                                    }
                                >
                                    <span>
                                        <Fab
                                            disabled={ContractStatus.VALID !== contract.status}
                                            color="primary"
                                            size="small"
                                            title={translate("buttons.close") as string}
                                            onClick={onClickClose}
                                        >
                                            <Stop />
                                        </Fab>
                                    </span>
                                </Tooltip>
                            </Grid>
                        )}
                    <Grid item xs="auto">
                        <IconButton color="default" size="small" onClick={onClickedMore}>
                            <MoreVertIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            }
        >
            <Tabs
                value={value}
                onChange={handleChange}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
            >
                {tabs.map((tab: ContractTab, index: number) => (
                    <Tab key={tab.name} label={tab.title} {...a11yProps(index)} />
                ))}
            </Tabs>
            <Grid
                container
                justifyContent="flex-start"
                alignItems="flex-start"
                style={{ minHeight: "calc(100vh - 230px)" }}
            >
                <Grid item xs={12} md={12} lg={12} xl={12}>
                    {tabs.map((tab: ContractTab, index: number) => (
                        <TabPanel key={tab.name} value={value} index={index}>
                            {tabContent(index)}
                        </TabPanel>
                    ))}
                </Grid>
            </Grid>
            {showEditDialog && ContractStatus.CLOSED !== contract.status && !isProvider && (
                <Dialog open maxWidth={"xs"}>
                    <DialogTitle>
                        <Typography>{contract ? translate("contracts.edit") : translate("contracts.new")}</Typography>
                    </DialogTitle>
                    <DialogContent>
                        <TenantContractForm
                            type="tenant"
                            isEdit={!!contract}
                            contractId={contract?.id}
                            getter={getContract}
                            documentGetter={getDocumentUrl}
                            onFinishSubmit={onUpdateContract}
                            onCancelSubmit={onCloseEditDialog}
                        />
                    </DialogContent>
                </Dialog>
            )}
            {showCloseDialog &&
                ContractStatus.CLOSED !== contract.status &&
                !isProvider &&
                context.isGranted("TenantContractsUpdate") && (
                    <Dialog open maxWidth={"xs"}>
                        <DialogTitle>
                            <Typography>{translate("se_contracts.view.close.title") as string}</Typography>
                        </DialogTitle>
                        <DialogContent>
                            {
                                translate("se_contracts.view.close.message", {
                                    status: translate(`se_contracts.status.${ContractStatus.CLOSED}`),
                                }) as string
                            }
                        </DialogContent>
                        <Divider />
                        <DialogActions>
                            <Button
                                variant="text"
                                color="primary"
                                size="large"
                                disabled={submitting}
                                onClick={() => setShowCloseDialog(false)}
                            >
                                {translate("buttons.cancel")}
                            </Button>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                size="large"
                                disabled={submitting}
                                onClick={onConfirmClose}
                            >
                                {translate("buttons.accept")}
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
            {gridAnchorEl && <ContractOverviewMenu anchor={gridAnchorEl} onClose={() => setGridAnchorEl(null)} />}
            <SuccessSnackbar message={success} onClose={onCloseSnackbars} />
            <ErrorSnackbar message={error} onClose={onCloseSnackbars} />
            <WarningSnackbar message={warning} onClose={onCloseSnackbars} />
        </Surface>
    );
}
